/**
 * IOT-HAL-1: Normalize vendor-specific sensor values + units to Kairos canonical.
 *
 * Each adapter calls the appropriate converter at its inbound boundary.
 * Canonical units per SensorType (matches DeviceContract.ts SensorType):
 *
 *   temperature_c    → °C
 *   humidity_pct     → %
 *   shock_g          → g
 *   tilt_deg         → °
 *   light_lux        → lux
 *   pressure_hpa     → hPa
 *   co2_ppm          → ppm
 *   door_open_bool   → boolean (0 / 1)
 *   gps_position     → GpsPosition object
 *   speed_kph        → km/h
 *   heading_deg      → °
 *   odometer_km      → km
 *   engine_rpm       → rpm
 *   fuel_level_pct   → %
 *   battery_voltage_v → V
 *   battery_soc_pct  → %
 *   cell_signal_dbm  → dBm
 */

/** Canonical unit strings keyed by SensorType. */
export const CANONICAL_UNITS = {
  temperature_c:     '°C',
  humidity_pct:      '%',
  shock_g:           'g',
  tilt_deg:          '°',
  light_lux:         'lux',
  pressure_hpa:      'hPa',
  co2_ppm:           'ppm',
  door_open_bool:    'bool',
  gps_position:      'gps',
  speed_kph:         'km/h',
  heading_deg:       '°',
  odometer_km:       'km',
  engine_rpm:        'rpm',
  fuel_level_pct:    '%',
  battery_voltage_v: 'V',
  battery_soc_pct:   '%',
  cell_signal_dbm:   'dBm',
};

/**
 * Convert a temperature value from a vendor unit to canonical °C.
 *
 * @param {number} value
 * @param {'celsius' | 'fahrenheit' | 'kelvin' | string} fromUnit
 * @returns {number}
 */
export function convertTemperature(value, fromUnit) {
  const u = fromUnit.toLowerCase();
  if (u === 'celsius' || u === 'c' || u === '°c') return value;
  if (u === 'fahrenheit' || u === 'f' || u === '°f') return (value - 32) * (5 / 9);
  if (u === 'kelvin' || u === 'k') return value - 273.15;
  throw new Error(`normalizeUnits: unknown temperature unit "${fromUnit}"`);
}

/**
 * Convert a speed value to canonical km/h.
 *
 * @param {number} value
 * @param {'kph' | 'mph' | 'ms' | 'knots' | string} fromUnit
 * @returns {number}
 */
export function convertSpeed(value, fromUnit) {
  const u = fromUnit.toLowerCase().replace(/\s/g, '');
  if (u === 'kph' || u === 'km/h' || u === 'kmh') return value;
  if (u === 'mph' || u === 'mi/h') return value * 1.60934;
  if (u === 'm/s' || u === 'ms') return value * 3.6;
  if (u === 'knots' || u === 'kn') return value * 1.852;
  throw new Error(`normalizeUnits: unknown speed unit "${fromUnit}"`);
}

/**
 * Convert a distance/odometer value to canonical km.
 *
 * @param {number} value
 * @param {'km' | 'mi' | 'm' | string} fromUnit
 * @returns {number}
 */
export function convertDistance(value, fromUnit) {
  const u = fromUnit.toLowerCase();
  if (u === 'km' || u === 'kilometers') return value;
  if (u === 'mi' || u === 'miles') return value * 1.60934;
  if (u === 'm' || u === 'meters') return value / 1000;
  throw new Error(`normalizeUnits: unknown distance unit "${fromUnit}"`);
}

/**
 * Convert a pressure value to canonical hPa.
 *
 * @param {number} value
 * @param {'hpa' | 'pa' | 'kpa' | 'psi' | 'bar' | string} fromUnit
 * @returns {number}
 */
export function convertPressure(value, fromUnit) {
  const u = fromUnit.toLowerCase();
  if (u === 'hpa' || u === 'hectopascals') return value;
  if (u === 'pa' || u === 'pascals') return value / 100;
  if (u === 'kpa' || u === 'kilopascals') return value * 10;
  if (u === 'psi') return value * 68.9476;
  if (u === 'bar') return value * 1000;
  if (u === 'mbar') return value;
  throw new Error(`normalizeUnits: unknown pressure unit "${fromUnit}"`);
}

/**
 * Return the canonical unit string for a sensor type.
 *
 * @param {string} sensorType
 * @returns {string}
 */
export function canonicalUnit(sensorType) {
  return CANONICAL_UNITS[sensorType] ?? '';
}
